"use client"

import { useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"
import { Button } from "./ui/button"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { User, Lock, Mail, Phone, CheckSquare, ImageIcon } from "lucide-react"

export function AddUserModal({ isOpen, onClose, onAddUser, roles }) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        role: "",
        password: "",
        profileImage: null,
    })

    const [errors, setErrors] = useState({})
    const [imageUrl, setImageUrl] = useState("")
    const [dataImage, setDataImage] = useState("")
    const [isUploading, setIsUploading] = useState(false)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))

        // Clear error when field is edited
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }))
        }
    }

    const handleRoleChange = (value) => {
        setFormData((prev) => ({ ...prev, role: value }))

        // Clear error when field is edited
        if (errors.role) {
            setErrors((prev) => ({ ...prev, role: null }))
        }
    }

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const data = new FormData()
            let image = e.target.files[0]
            data.append("file", image)
            data.append("upload_preset", "Tumblr") // Replace with your upload preset
            data.append("cloud_name", "dgqt5ockx") // Replace with your cloud name

            setFormData((prev) => ({ ...prev, profileImage: e.target.files[0] }))
            setDataImage(data)
            
            // Clear error when file is selected
            if (errors.profileImage) {
                setErrors((prev) => ({ ...prev, profileImage: null }))
            }
        }
    }

    const uploadToCloudinary = async (data) => {
        try {
            setIsUploading(true)
            const response = await axios.post(
                "https://api.cloudinary.com/v1_1/dgqt5ockx/image/upload", // Replace with your cloud name
                data
            )
            setImageUrl(response.data.url)
            toast.success("Image uploaded successfully!")
            return response.data.url
        } catch (error) {
            console.error("Cloudinary upload error:", error)
            const errorMessage = "Failed to upload image"
            setErrors((prev) => ({ ...prev, profileImage: errorMessage }))
            toast.error(errorMessage)
            return null
        } finally {
            setIsUploading(false)
        }
    }

    const validateForm = () => {
        const newErrors = {}

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = "Name is required"
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = "Email is required"
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email is invalid"
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = "Password is required"
        } else if (formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters"
        }

        // Phone / Contact is optional; validate format only when provided
        if (formData.phone.trim() && !/^\d+$/.test(formData.phone)) {
            newErrors.phone = "Phone number must contain only numbers"
        }

        // Role validation
        if (!formData.role) {
            newErrors.role = "Role is required"
        }

        setErrors(newErrors)

        // Show toast messages for validation errors
        if (Object.keys(newErrors).length > 0) {
            const firstError = Object.values(newErrors)[0]
            toast.error(firstError)
            return false
        }

        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (validateForm()) {
            let finalImageUrl = "" // Photo optional — empty if not uploaded

            // Upload image to Cloudinary if selected
            if (dataImage) {
                const uploadedUrl = await uploadToCloudinary(dataImage)
                if (uploadedUrl) {
                    finalImageUrl = uploadedUrl
                }
            }

            try {
                onAddUser({
                    ...formData,
                    id: Date.now(),
                    lastLogin: "Never",
                    mailStatus: "Disab",
                    profileImage: finalImageUrl,
                })

                // Reset form
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    role: "",
                    password: "",
                    profileImage: null,
                })
                setImageUrl("")
                setDataImage("")
                setErrors({})
                onClose()
            } catch (error) {
                toast.error("Failed to add user. Please try again.")
            }
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md p-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-2">
                    <DialogTitle className="text-xl font-medium">Add User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <User className="h-5 w-5 text-gray-500" />
                            </div>
                            <Input
                                id="name"
                                name="name"
                                placeholder="Name *"
                                value={formData.name}
                                onChange={handleChange}
                                className={`pl-10 py-5 ${errors.name ? "border-red-500" : ""}`}
                                required
                            />
                            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-500" />
                            </div>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                placeholder="Password *"
                                value={formData.password}
                                onChange={handleChange}
                                className={`pl-10 py-5 bg-blue-50 ${errors.password ? "border-red-500" : ""}`}
                                required
                            />
                            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-500" />
                            </div>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Email *"
                                value={formData.email}
                                onChange={handleChange}
                                className={`pl-10 py-5 bg-blue-50 ${errors.email ? "border-red-500" : ""}`}
                                required
                            />
                            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <Phone className="h-5 w-5 text-gray-500" />
                            </div>
                            <Input
                                id="phone"
                                name="phone"
                                placeholder="Contact"
                                value={formData.phone}
                                onChange={handleChange}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                                        e.preventDefault()
                                    }
                                }}
                                className={`pl-10 py-5 ${errors.phone ? "border-red-500" : ""}`}
                            />
                            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <CheckSquare className="h-5 w-5 text-gray-500" />
                            </div>

                            <Select value={formData.role} onValueChange={handleRoleChange} required>
                                <SelectTrigger className={`pl-10 py-5 h-[50px] ${errors.role ? "border-red-500" : ""}`}>
                                    <SelectValue placeholder="Role *" />
                                </SelectTrigger>

                                <SelectContent>
                                    {roles.map(({ id, role }) => (
                                        <SelectItem key={id} value={role}>
                                            {role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {errors.role && <p className="text-xs text-red-500 mt-1">{errors.role}</p>}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <ImageIcon className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="pl-10 flex items-center">
                                <label className="inline-block w-full">
                                    <span className="sr-only">Choose file</span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                    />
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="mr-2 text-sm"
                                                disabled={isUploading}
                                                onClick={() => document.querySelector('input[type="file"]').click()}
                                            >
                                                {isUploading ? "Uploading..." : "Choose File"}
                                            </Button>
                                            <span className="text-sm text-gray-500">
                                                {formData.profileImage ? formData.profileImage.name : "No file chosen"}
                                            </span>
                                        </div>
                                        {imageUrl && (
                                            <img
                                                src={imageUrl}
                                                alt="Preview"
                                                className="w-10 h-10 rounded-full object-cover ml-2"
                                            />
                                        )}
                                    </div>
                                </label>
                            </div>
                            {errors.profileImage && <p className="text-xs text-red-500 mt-1">{errors.profileImage}</p>}
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-medium py-5"
                        disabled={isUploading}
                    >
                        {isUploading ? "Uploading..." : "Save"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    )
}